/**
 * PATH: packages/ui/src/components/adaptive-overlay/popoverGeometry.ts
 * FILE_ID: UI-ADAPTIVE-OVERLAY-GEO-001
 * PURPOSE: Pure viewport clamp for AdaptiveOverlay popovers.
 * WHY: Absolute-positioned menus (job info, New Emails) clipped on phones;
 *   flip above / shift left before open so the panel stays fully visible.
 */

export interface PopoverGeometry {
  left: number
  top: number
  width: number
  maxHeight: number
  /** Which vertical side of the anchor the panel hangs from. */
  side: 'above' | 'below'
}

const MARGIN = 8
const GAP = 6

/**
 * f(anchor rect, width, heightHint, viewport) → fixed geometry inside the
 * viewport. Prefer below + left-aligned to the trigger; flip above when the
 * lower side is too short; slide left when the right edge would clip.
 */
export function computePopoverGeometry(args: {
  anchorLeft: number
  anchorRight: number
  anchorTop: number
  anchorBottom: number
  requestedWidth: number
  /** Expected panel height used to decide flip-above before paint. */
  heightHint?: number
  viewportWidth: number
  viewportHeight: number
}): PopoverGeometry {
  const heightHint = Math.max(96, args.heightHint ?? 200)
  const width = Math.min(
    args.requestedWidth,
    Math.max(160, args.viewportWidth - MARGIN * 2),
  )

  // Prefer left-align to the trigger; if that overflows right, hang from
  // the trigger's right edge (extend left). Then clamp into the viewport.
  let left = args.anchorLeft
  if (left + width > args.viewportWidth - MARGIN) {
    left = args.anchorRight - width
  }
  left = Math.max(MARGIN, Math.min(left, args.viewportWidth - width - MARGIN))

  const spaceBelow = args.viewportHeight - args.anchorBottom - GAP - MARGIN
  const spaceAbove = args.anchorTop - GAP - MARGIN
  const side: 'above' | 'below' =
    spaceBelow < heightHint && spaceAbove > spaceBelow ? 'above' : 'below'

  if (side === 'below') {
    const top = args.anchorBottom + GAP
    return {
      left,
      top,
      width,
      maxHeight: Math.max(96, args.viewportHeight - top - MARGIN),
      side,
    }
  }

  const maxHeight = Math.max(96, spaceAbove)
  // Place so the panel's bottom sits just above the trigger.
  const top = Math.max(MARGIN, args.anchorTop - GAP - maxHeight)
  return { left, top, width, maxHeight, side }
}

export function popoverGeometryFromAnchor(
  anchor: HTMLElement | null,
  requestedWidth: number,
  heightHint?: number,
): PopoverGeometry | null {
  if (anchor == null) return null
  const rect = anchor.getBoundingClientRect()
  // display:none / hidden phone+tablet twins report 0x0. Painting that
  // rect clamps to the top-left (BUG-2026-09-01-QA-ASSIGNEE-DOUBLE-POPUP).
  if (rect.width < 1 || rect.height < 1) return null
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768
  return computePopoverGeometry({
    anchorLeft: rect.left,
    anchorRight: rect.right,
    anchorTop: rect.top,
    anchorBottom: rect.bottom,
    requestedWidth,
    heightHint,
    viewportWidth,
    viewportHeight,
  })
}
